import {
  PlayerInput,
  EMPTY_INPUT,
  TrackPoint,
  MAX_SPEED,
  KART_RADIUS,
  ItemType,
  BananaState,
  CoinState,
} from '../../shared/types.js';
import { findClosestPoint } from '../../shared/track.js';

// ============================================================
// Bot State
// ============================================================

interface BotState {
  lookAhead: number;       // how many path points to look ahead (10-30)
  speedFactor: number;     // target speed as fraction of max (0.65-1.0)
  steeringNoise: number;   // random wobble added to steering (0-0.1)
  itemDelay: number;       // seconds before using a picked-up item
  itemTimer: number;       // current countdown to item use
  skillLevel: number;      // 0.5-1.0, affects all parameters
  brakeThreshold: number;  // angle threshold for braking in turns (0.3-0.8 radians)
  stuckTimer: number;      // how long the bot has been nearly stationary
}

// ============================================================
// Kart info subset passed to bot AI
// ============================================================

interface BotKartInfo {
  id: string;
  x: number;
  z: number;
  yaw: number;
  speed: number;
  item: ItemType | null;
  stunTimer: number;
  position: number;
  coins: number;
  starTimer: number;
  orbitItems: number;
  heldItemBehind: ItemType | null;
  rouletteTimer: number;
}

// ============================================================
// Bot AI
// ============================================================

export class BotAI {
  private states: Map<string, BotState> = new Map();

  /** Initialize bot state with deterministic skill based on rank.
   *  rank 0 = best bot, rank totalBots-1 = worst bot. */
  initBot(botId: string, rank: number, totalBots: number): void {
    const t = rank / Math.max(totalBots - 1, 1); // 0 (best) → 1 (worst)
    const skill = 1.0 - t;
    const isElite = rank < 3;

    this.states.set(botId, {
      lookAhead: Math.floor(8 + skill * 22),                          // 8-30
      speedFactor: isElite ? 1.0 : 0.55 + skill * 0.45,              // top 3: max, rest: 0.55-0.86
      steeringNoise: isElite ? 0 : 0.1 * (1 - skill),               // top 3: none, rest: 0-0.1
      itemDelay: isElite ? 0.3 : 0.5 + (1 - skill) * 2.5,           // top 3: instant, rest: 0.5-3.0
      itemTimer: 0,
      skillLevel: skill,
      brakeThreshold: isElite ? 0.25 : 0.3 + (1 - skill) * 0.5,    // top 3: aggressive, rest: 0.3-0.8
      stuckTimer: 0,
    });
  }

  /** Generate input for a bot kart each simulation tick */
  generateInput(
    botId: string,
    kart: BotKartInfo,
    path: TrackPoint[],
    numPoints: number,
    dt: number,
    allKarts: BotKartInfo[],
    bananas: Array<{ x: number; z: number }>,
    activeCoins: Array<{ x: number; z: number }>,
  ): PlayerInput {
    const state = this.states.get(botId);
    if (!state) return { ...EMPTY_INPUT, forward: true, trick: false };

    const kartX = kart.x;
    const kartZ = kart.z;
    const kartYaw = kart.yaw;
    const kartSpeed = kart.speed;
    const kartItem = kart.item;
    const kartStunTimer = kart.stunTimer;
    const kartPosition = kart.position;
    const totalKarts = allKarts.length;

    // If stunned, do nothing
    if (kartStunTimer > 0) {
      state.stuckTimer = 0;
      return { ...EMPTY_INPUT, trick: false };
    }

    // Find current position on track
    const closest = findClosestPoint(path, kartX, kartZ);

    // ── Stuck detection & recovery ──
    if (Math.abs(kartSpeed) < 1.0) {
      state.stuckTimer += dt;
    } else {
      state.stuckTimer = 0;
    }

    // If stuck for a while, reverse briefly and steer toward center
    if (state.stuckTimer > 0.5) {
      const left = closest.lateralOffset > 0;
      const right = closest.lateralOffset < 0;
      const reversing = state.stuckTimer < 1.5 || (state.stuckTimer % 2.0 < 1.0);
      return {
        forward: !reversing,
        backward: reversing,
        left,
        right,
        drift: false,
        item: false,
        trick: false,
      };
    }

    // ── Compute target point: follow center line exactly ──

    // Short look-ahead for steering so bots follow the road center
    // instead of cutting corners (which causes wall collisions)
    const steerAhead = Math.min(state.lookAhead, 8);
    const targetIdx = (closest.index + steerAhead) % numPoints;
    const target = path[targetIdx];

    // Always correct toward center proportional to lateral offset.
    // lateralOffset > 0 = right of center → push target left (−nx)
    const cp = path[closest.index];
    let correctedTargetX = target.x - cp.nx * closest.lateralOffset * 2.0;
    let correctedTargetZ = target.z - cp.nz * closest.lateralOffset * 2.0;

    // ── Banana avoidance: steer away from nearby bananas ──
    let bananaAvoidX = 0;
    let bananaAvoidZ = 0;
    if (bananas.length > 0) {
      for (const banana of bananas) {
        const bdx = kartX - banana.x;
        const bdz = kartZ - banana.z;
        const bDist = Math.sqrt(bdx * bdx + bdz * bdz);
        if (bDist < 8 && bDist > 0.1) {
          // Check if banana is ahead of us
          const dirToBanana = Math.atan2(banana.z - kartZ, banana.x - kartX);
          let bAngleDiff = dirToBanana - kartYaw;
          while (bAngleDiff > Math.PI) bAngleDiff -= Math.PI * 2;
          while (bAngleDiff < -Math.PI) bAngleDiff += Math.PI * 2;
          if (Math.abs(bAngleDiff) < 0.8) {
            // Banana is ahead: push target away from banana
            const avoidStrength = (8 - bDist) / 8 * 5;
            bananaAvoidX += (bdx / bDist) * avoidStrength;
            bananaAvoidZ += (bdz / bDist) * avoidStrength;
          }
        }
      }
      correctedTargetX += bananaAvoidX;
      correctedTargetZ += bananaAvoidZ;
    }

    // ── Coin attraction: steer toward nearest coin when no item held ──
    if (kartItem === null && kart.orbitItems <= 0 && kart.heldItemBehind === null &&
        kart.rouletteTimer <= 0 && kart.coins < 10 && activeCoins.length > 0) {
      let nearestCoinDist = Infinity;
      let nearestCoinX = 0;
      let nearestCoinZ = 0;
      for (const coin of activeCoins) {
        const cdx = coin.x - kartX;
        const cdz = coin.z - kartZ;
        const cDist = Math.sqrt(cdx * cdx + cdz * cdz);
        // Only consider coins that are somewhat ahead
        if (cDist < nearestCoinDist && cDist < 15) {
          const dirToCoin = Math.atan2(cdz, cdx);
          let cAngleDiff = dirToCoin - kartYaw;
          while (cAngleDiff > Math.PI) cAngleDiff -= Math.PI * 2;
          while (cAngleDiff < -Math.PI) cAngleDiff += Math.PI * 2;
          if (Math.abs(cAngleDiff) < 1.0) {
            nearestCoinDist = cDist;
            nearestCoinX = coin.x;
            nearestCoinZ = coin.z;
          }
        }
      }
      if (nearestCoinDist < 15) {
        // Slight attraction toward the coin
        const coinAttractionStrength = 2.0;
        const cdx = nearestCoinX - kartX;
        const cdz = nearestCoinZ - kartZ;
        const cLen = Math.sqrt(cdx * cdx + cdz * cdz) || 1;
        correctedTargetX += (cdx / cLen) * coinAttractionStrength;
        correctedTargetZ += (cdz / cLen) * coinAttractionStrength;
      }
    }

    // Calculate desired angle to corrected target
    const dx = correctedTargetX - kartX;
    const dz = correctedTargetZ - kartZ;
    const desiredYaw = Math.atan2(dz, dx);

    // Calculate angle difference (shortest path)
    let angleDiff = desiredYaw - kartYaw;
    while (angleDiff > Math.PI) angleDiff -= Math.PI * 2;
    while (angleDiff < -Math.PI) angleDiff += Math.PI * 2;

    // Add slight steering noise for less-skilled bots
    angleDiff += (Math.random() - 0.5) * state.steeringNoise;

    // Steering: turn toward the target
    const steerThreshold = 0.05;
    const left = angleDiff > steerThreshold;
    const right = angleDiff < -steerThreshold;

    // Braking: check if upcoming track curves sharply
    const aheadIdx1 = (closest.index + state.lookAhead) % numPoints;
    const aheadIdx2 = (closest.index + state.lookAhead * 2) % numPoints;
    const p1 = path[aheadIdx1];
    const p2 = path[aheadIdx2];
    const turnAngle = Math.abs(
      Math.atan2(p2.dz, p2.dx) - Math.atan2(p1.dz, p1.dx)
    );
    const normalizedTurn = turnAngle > Math.PI ? Math.PI * 2 - turnAngle : turnAngle;

    const shouldBrake =
      normalizedTurn > state.brakeThreshold && kartSpeed > MAX_SPEED * 0.6;

    // Target speed — slow down when far off-center
    let effectiveSpeedFactor = state.speedFactor;
    const halfRoad = 7;
    const offsetRatio = Math.min(Math.abs(closest.lateralOffset) / halfRoad, 1.0);
    if (offsetRatio > 0.6) {
      effectiveSpeedFactor *= 0.7;
    }

    const targetSpeed = MAX_SPEED * effectiveSpeedFactor;
    const forward = kartSpeed < targetSpeed && !shouldBrake;
    const backward = shouldBrake;

    // Drift: use when turning sharply at high speed
    const drift = Math.abs(angleDiff) > 0.4 && kartSpeed > MAX_SPEED * 0.6;

    // ── Item use logic with smart strategy ──
    let useItem = false;
    if (kartItem !== null) {
      state.itemTimer += dt;
      if (state.itemTimer >= state.itemDelay) {
        useItem = this.shouldUseItem(kartItem, kartPosition, totalKarts, kart, allKarts);
        if (useItem) {
          state.itemTimer = 0;
        }
      }
    } else {
      state.itemTimer = 0;
    }

    // Also handle orbit items: fire when delay passed and someone is ahead
    if (kart.orbitItems > 0 && kartItem !== null) {
      state.itemTimer += dt;
      if (state.itemTimer >= state.itemDelay) {
        useItem = true;
        state.itemTimer = 0;
      }
    }

    return {
      forward,
      backward,
      left,
      right,
      drift,
      item: useItem,
      trick: false,
    };
  }

  /** Decide whether to use the current item based on strategy */
  private shouldUseItem(
    item: ItemType,
    position: number,
    totalKarts: number,
    kart: BotKartInfo,
    allKarts: BotKartInfo[],
  ): boolean {
    switch (item) {
      // Instant-use items: always use immediately
      case ItemType.Lightning:
      case ItemType.Star:
        return true;

      // Blooper: use when in mid-to-back position
      case ItemType.Blooper:
        return position > Math.ceil(totalKarts * 0.3);

      // Speed boost: always use
      case ItemType.SpeedBoost:
        return true;

      // Green shell: fire when someone is ahead and roughly in line
      case ItemType.GreenShell:
      case ItemType.TripleGreenShell:
        return this.isKartAheadInLine(kart, allKarts, 30, 0.4);

      // Red shell: fire when not in 1st (targets kart ahead)
      case ItemType.RedShell:
      case ItemType.TripleRedShell:
        return position > 1;

      // Blue shell: use only when not in 1st
      case ItemType.BlueShell:
        return position > 1;

      // Banana: drop when in lead positions (protect rear)
      case ItemType.Banana:
      case ItemType.TripleBanana:
        return position <= Math.ceil(totalKarts * 0.4) || Math.random() < 0.3;

      // Legacy projectile
      case ItemType.Projectile:
        return this.isKartAheadInLine(kart, allKarts, 40, 0.5);

      default:
        return true;
    }
  }

  /** Check if any kart is ahead within distance and within a cone angle */
  private isKartAheadInLine(
    kart: BotKartInfo,
    allKarts: BotKartInfo[],
    maxDist: number,
    coneAngle: number,
  ): boolean {
    const fwdX = Math.cos(kart.yaw);
    const fwdZ = Math.sin(kart.yaw);

    for (const other of allKarts) {
      if (other.id === kart.id) continue;
      const dx = other.x - kart.x;
      const dz = other.z - kart.z;
      const dist = Math.sqrt(dx * dx + dz * dz);
      if (dist > maxDist || dist < 1) continue;

      // Check if ahead (in front of bot)
      const dot = (dx * fwdX + dz * fwdZ) / dist;
      if (dot > 0) {
        // Check cone
        const angle = Math.acos(Math.min(1, Math.abs(dot)));
        if (angle < coneAngle) {
          return true;
        }
      }
    }
    return false;
  }

  /** Remove a bot's state */
  removeBot(botId: string): void {
    this.states.delete(botId);
  }
}
